// Contact.jsx
import { useState } from "react";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    country: "",
    subject: "",
    message: ""
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Handle Input Changes
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
    // Clear error when user starts typing
    if (errors[id]) {
      setErrors(prev => ({ ...prev, [id]: "" }));
    }
  };

  // Validate Form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = "Name is required";
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    
    if (!formData.country.trim()) newErrors.country = "Country is required";
    if (!formData.subject.trim()) newErrors.subject = "Subject is required";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    
    return newErrors;
  };

  // Handle Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    
    if (Object.keys(newErrors).length === 0) {
      setIsLoading(true);
      
      // Simulate API call
      setTimeout(() => {
        console.log("Form Data:", formData);
        setIsSubmitted(true);
        setFormData({
          name: "",
          email: "",
          country: "",
          subject: "",
          message: ""
        });
        setIsLoading(false);
        
        // Hide success message after 5 seconds
        setTimeout(() => setIsSubmitted(false), 5000);
      }, 1500);
      
      // Real API call example:
      // try {
      //   const response = await axios.post('/api/contact', formData);
      //   if (response.data.success) {
      //     setIsSubmitted(true);
      //     setFormData({...});
      //   }
      // } catch (error) {
      //   console.error("Error:", error);
      // } finally {
      //   setIsLoading(false);
      // }
    } else {
      setErrors(newErrors);
      // Scroll to first error
      const firstError = Object.keys(newErrors)[0];
      document.getElementById(firstError)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <div className="fixed-box min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 py-12 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-7xl">
        
        {/* About List Section - Information Cards */}
        <div className="about-list grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          
          {/* Website Card */}
          <div className="about-list-item bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 transform transition-all duration-300 hover:scale-105 hover:shadow-xl">
            <div className="flex items-center space-x-4">
              <div className="global w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.66 0 3-4 3-9s-1.34-9-3-9m0 18c-1.66 0-3-4-3-9s1.34-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
              </div>
              <div>
                <p className="text-gray-600 dark:text-gray-300 text-sm">Official Website</p>
                <span className="text-blue-600 dark:text-blue-400 font-semibold">www.anabolicpharma.net</span>
              </div>
            </div>
          </div>

          {/* Warning Card */}
          <div className="about-list-item bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 transform transition-all duration-300 hover:scale-105 hover:shadow-xl border-l-4 border-red-500">
            <div className="flex items-start space-x-4">
              <div className="triangle w-12 h-12 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                  <span className="font-bold text-red-600 dark:text-red-400">Warning:</span> All other domains that seem to be associated with Anabolic Pharma present counterfeit products.
                </p>
              </div>
            </div>
          </div>

          {/* Email Card */}
          <div className="about-list-item bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 transform transition-all duration-300 hover:scale-105 hover:shadow-xl">
            <div className="flex items-center space-x-4">
              <div className="email w-12 h-12 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-gray-600 dark:text-gray-300 text-sm">Email Us</p>
                <span className="text-green-600 dark:text-green-400 font-semibold">support@anabolicpharma.net</span>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form Section */}
        <div className="about-form bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
          <div className="about-form-left">
            {/* Hero Section */}
            <div className="about-des bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-10 text-white text-center">
              <h3 className="text-3xl md:text-4xl font-bold mb-4">Leave Your Message!</h3>
              <p className="text-lg opacity-95 max-w-2xl mx-auto">
                We are always excited to hear from you, so please feel free to contact us 
                and we will gladly reply to any and all queries you may have.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name Field */}
                <div className="about-form-item">
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input 
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    className={`form-item-input w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border ${
                      errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'
                    } rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-300 text-gray-900 dark:text-white`}
                    placeholder="Enter your full name"
                  />
                  {errors.name && (
                    <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                  )}
                </div>

                {/* Email Field */}
                <div className="about-form-item">
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    E-mail <span className="text-red-500">*</span>
                  </label>
                  <input 
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`form-item-input w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border ${
                      errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'
                    } rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-300 text-gray-900 dark:text-white`}
                    placeholder="your@email.com"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                  )}
                </div>

                {/* Country Field */}
                <div className="about-form-item">
                  <label htmlFor="country" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <input 
                    id="country"
                    type="text"
                    value={formData.country}
                    onChange={handleChange}
                    className={`form-item-input w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border ${
                      errors.country ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'
                    } rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-300 text-gray-900 dark:text-white`}
                    placeholder="Your country"
                  />
                  {errors.country && (
                    <p className="text-red-500 text-xs mt-1">{errors.country}</p>
                  )}
                </div>

                {/* Subject Field */}
                <div className="about-form-item">
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <input 
                    id="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`form-item-input w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border ${
                      errors.subject ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'
                    } rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-300 text-gray-900 dark:text-white`}
                    placeholder="Message subject"
                  />
                  {errors.subject && (
                    <p className="text-red-500 text-xs mt-1">{errors.subject}</p>
                  )}
                </div>
              </div>

              {/* Message Field */}
              <div className="about-form-item">
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea 
                  id="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  className={`form-item-input w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border ${
                    errors.message ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'
                  } rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-300 text-gray-900 dark:text-white resize-none`}
                  placeholder="Write your message here..."
                ></textarea>
                {errors.message && (
                  <p className="text-red-500 text-xs mt-1">{errors.message}</p>
                )}
              </div>

              {/* Submit Button */}
              <div className="text-center pt-4">
                <button 
                  type="submit"
                  disabled={isLoading}
                  className="form-item-btn px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  id="kontaButt"
                >
                  {isLoading ? (
                    <span className="flex items-center space-x-2">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>SENDING...</span>
                    </span>
                  ) : (
                    "SEND MESSAGE"
                  )}
                </button>
                
                {/* Success Message */}
                {isSubmitted && (
                  <div className="mt-6 p-4 bg-green-500/10 border border-green-500 rounded-lg animate-fade-in">
                    <p className="text-green-600 dark:text-green-400 font-semibold">
                      ✓ Thank you! Your message has been sent successfully. We'll get back to you soon!
                    </p>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}